import type { ReactNode } from 'react';
import type { LueftungGrundlagen } from '../../api';
import { ContentSection, DataTable, ErrorState, StatusBadge, bestaetigung, herkunft, type Spalte } from '../../muster';
import { ANNAHMEN_ANKER, ANNAHMEN_TITEL } from './pruefung';

type Raumakustik = LueftungGrundlagen['raumakustik'][number];

const SPALTEN: Spalte<Raumakustik>[] = [
  { schluessel: 'nutzung', titel: 'Nutzungsart', wert: (r) => r.bezeichnung, immerSichtbar: true },
  { schluessel: 'schluessel', titel: 'Schlüssel', wert: (r) => r.schluessel },
  { schluessel: 'kategorie', titel: 'Raumkategorie', wert: (r) => r.raumkategorie ?? '' },
  { schluessel: 'nachhall', titel: 'Nachhallzeit', art: 'zahl', einheit: 's', nachkomma: 2, wert: (r) => r.nachhallzeitS },
  { schluessel: 'ziel', titel: 'Zielpegel', art: 'zahl', einheit: 'dB(A)', nachkomma: 0, wert: (r) => r.zielpegelDbA },
  {
    schluessel: 'herkunft', titel: 'Herkunft', art: 'status', wert: (r) => herkunft(r.herkunft).text,
    inhalt: (r) => <StatusBadge {...herkunft(r.herkunft)} klein />,
  },
  { schluessel: 'quelle', titel: 'Quelle / Norm', wert: (r) => r.quelle ?? r.hinweis ?? '' },
  {
    schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (r) => bestaetigung(r.istBestaetigt).text,
    inhalt: (r) => <StatusBadge {...bestaetigung(r.istBestaetigt)} klein />,
  },
];

/**
 * Abschnitt „Annahmen und Vorgaben“ (S1.6) der Schallseiten: Nachhallzeit und
 * Zielpegel je Nutzungsart aus `lueftung_raumakustik` - die Werte, gegen die
 * „über Ziel“ steht und aus denen das Diffusfeld entsteht. Nur lesend; die
 * Werte pflegt die Fachplanung in den Grundlagen der Lüftung.
 */
export function RaumakustikAbschnitt({ daten, fehler, zusatz }: {
  daten: LueftungGrundlagen['raumakustik'] | null;
  /** Die Grundlagen konnten nicht geladen werden (ein eigener Zustand, kein Seitenfehler). */
  fehler?: boolean;
  zusatz?: ReactNode;
}) {
  return (
    <ContentSection
      id={ANNAHMEN_ANKER}
      titel={ANNAHMEN_TITEL}
      beschreibung="Nachhallzeit und Zielpegel je Nutzungsart, wie die Rechnung sie ansetzt. Unbestätigte Werte sind Annahmen; eine Überschreitung gegen sie ist eine Anzeige, kein Mangel."
    >
      {fehler ? (
        <ErrorState klein meldung="Die Grundlagen der Lüftung (Raumakustik) konnten nicht abgerufen werden." />
      ) : (
        <DataTable<Raumakustik>
          name="Raumakustik je Nutzungsart"
          spalten={SPALTEN}
          zeilen={daten ?? []}
          schluessel={(r) => r.schluessel}
          laedt={daten === null}
          leer={<span>Keine Raumakustik hinterlegt - ohne Nutzungsart gilt der Rückfall des Zielpegels.</span>}
          dicht
        />
      )}
      {zusatz}
    </ContentSection>
  );
}
